import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/stores/redux/store';

export type CallStatus =
  | 'idle'
  | 'outgoing'
  | 'incoming'
  | 'connecting'
  | 'in_call'
  | 'ended';

export type CallKind = 'voice' | 'video' | 'screen';

export interface CallMediaState {
  mic: boolean;
  camera: boolean;
  screen: boolean;
  remoteHasVideo: boolean;
  remoteHasScreen: boolean;
  penActive: boolean;
}

export interface IncomingSignalEvent {
  call_id: string;
  from_user_id: string;
  signal_type:
    | 'invite'
    | 'accept'
    | 'reject'
    | 'cancel'
    | 'end'
    | 'offer'
    | 'answer'
    | 'ice'
    // Mesh roster handshake: a participant announces it is in the call so the
    // others establish a peer connection with it (enables group + mid-call join).
    | 'join';
  payload: any;
  timestamp: number;
  // Invite-specific extras
  kind?: CallKind;
  dm_conversation_id?: string;
}

interface CallsState {
  status: CallStatus;
  callId: string | null;
  /** Primary peer (the first/only other participant). Kept for 1:1 paths +
   *  the ringing modal; for a group call it's participants[0]. */
  peerUserId: string | null;
  /** All OTHER participants in the call (excludes self). For a 1:1 call this
   *  is a single-element list; for a mesh group call it's everyone else. */
  participants: string[];
  dmConversationId: string | null;
  kind: CallKind | null;
  isCaller: boolean;
  startedAt: number | null;
  endedAt: number | null;
  endReason: 'ended' | 'rejected' | 'missed' | 'cancelled' | 'failed' | null;
  media: CallMediaState;
  // FIFO queue of incoming signals. Multiple signals (esp. ICE candidates)
  // can arrive in the same tick; storing only the latest dropped earlier
  // ones (e.g. an offer was overwritten by the next ICE), which broke the
  // callee's ICE establishment intermittently. The hook drains this in
  // arrival order with a mutex.
  signalQueue: IncomingSignalEvent[];
  error: string | null;
}

const initialMedia: CallMediaState = {
  mic: false,
  camera: false,
  screen: false,
  remoteHasVideo: false,
  remoteHasScreen: false,
  penActive: false,
};

const initialState: CallsState = {
  status: 'idle',
  callId: null,
  peerUserId: null,
  participants: [],
  dmConversationId: null,
  kind: null,
  isCaller: false,
  startedAt: null,
  endedAt: null,
  endReason: null,
  media: initialMedia,
  signalQueue: [],
  error: null,
};

const callsSlice = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    startOutgoing: (
      state,
      action: PayloadAction<{
        callId: string;
        peerUserId: string;
        dmConversationId: string;
        kind: CallKind;
        /** All other participants for a group call. Defaults to [peerUserId]. */
        participants?: string[];
      }>
    ) => {
      // Idempotent: ignore a duplicate start for a call already in progress.
      // The popup's AutoStart can re-dispatch (component re-mount resets its
      // dispatchedRef); without this guard the second dispatch resets the FSM
      // back to 'outgoing' mid-negotiation and the call drops (audio-flake
      // root cause). A genuinely new call (different callId, or after ended)
      // still proceeds.
      if (
        state.status !== 'idle' &&
        state.status !== 'ended' &&
        state.callId === action.payload.callId
      ) {
        return;
      }
      state.status = 'outgoing';
      state.callId = action.payload.callId;
      state.peerUserId = action.payload.peerUserId;
      state.participants = action.payload.participants ?? [action.payload.peerUserId];
      state.dmConversationId = action.payload.dmConversationId;
      state.kind = action.payload.kind;
      state.isCaller = true;
      state.startedAt = null;
      state.endedAt = null;
      state.endReason = null;
      state.media = {
        ...initialMedia,
        mic: true,
        camera: action.payload.kind === 'video',
        screen: action.payload.kind === 'screen',
      };
      state.error = null;
    },

    receiveIncoming: (
      state,
      action: PayloadAction<{
        callId: string;
        peerUserId: string;
        dmConversationId: string;
        kind: CallKind;
        /** Full roster (other participants besides self) for a group call,
         *  as advertised in the invite. Defaults to [peerUserId] (1:1). */
        participants?: string[];
      }>
    ) => {
      // Auto-reject if already in another call
      if (state.status !== 'idle' && state.status !== 'ended') return;
      state.status = 'incoming';
      state.callId = action.payload.callId;
      state.peerUserId = action.payload.peerUserId;
      state.participants = action.payload.participants ?? [action.payload.peerUserId];
      state.dmConversationId = action.payload.dmConversationId;
      state.kind = action.payload.kind;
      state.isCaller = false;
      state.media = initialMedia;
      state.error = null;
    },

    setConnecting: (state) => {
      state.status = 'connecting';
    },

    setInCall: (state) => {
      state.status = 'in_call';
      // Set the start time only once. connectionState can flip
      // connected → disconnected → connected on ICE restarts; re-stamping
      // startedAt each time would reset the call timer and undercount duration.
      if (state.startedAt == null) {
        state.startedAt = Date.now();
      }
    },

    setMediaState: (state, action: PayloadAction<Partial<CallMediaState>>) => {
      state.media = { ...state.media, ...action.payload };
    },

    setPenActive: (state, action: PayloadAction<boolean>) => {
      state.media.penActive = action.payload;
    },

    /** Add a participant to an ongoing call (mid-call invite). */
    addParticipant: (state, action: PayloadAction<string>) => {
      if (action.payload && !state.participants.includes(action.payload)) {
        state.participants.push(action.payload);
      }
    },

    endCall: (
      state,
      action: PayloadAction<{ reason: NonNullable<CallsState['endReason']> }>
    ) => {
      state.status = 'ended';
      state.endedAt = Date.now();
      state.endReason = action.payload.reason;
    },

    resetCall: () => initialState,

    pushIncomingSignal: (state, action: PayloadAction<IncomingSignalEvent>) => {
      state.signalQueue.push(action.payload);
    },

    /** Removes the head of the queue (the signal we just finished processing). */
    shiftIncomingSignal: (state) => {
      state.signalQueue.shift();
    },

    /** Drops every queued signal (used on call teardown / reset). */
    clearIncomingSignals: (state) => {
      state.signalQueue = [];
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});

export const {
  startOutgoing,
  receiveIncoming,
  setConnecting,
  setInCall,
  setMediaState,
  setPenActive,
  addParticipant,
  endCall,
  resetCall,
  pushIncomingSignal,
  shiftIncomingSignal,
  clearIncomingSignals,
  setError,
} = callsSlice.actions;

// Selectors
export const selectCallStatus = (state: RootState) => state.calls.status;
export const selectCall = (state: RootState) => state.calls;
export const selectCallMedia = (state: RootState) => state.calls.media;
export const selectIsInCall = (state: RootState) =>
  state.calls.status === 'in_call' || state.calls.status === 'connecting';
/** First (oldest) queued signal, or `null` if the queue is empty. */
export const selectIncomingSignal = (state: RootState) =>
  state.calls.signalQueue[0] ?? null;
export const selectSignalQueueLength = (state: RootState) =>
  state.calls.signalQueue.length;

export default callsSlice.reducer;
